import {useEffect, useState} from "react";
import {Box, CircularProgress, IconButton, Modal, Pagination, Paper, TextField, Typography,} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import {useAlert} from "../context/AlertContext";
import AppTableHeaderCell from "./AppTableHeaderCell";
import {assignUserToCourse, getCourseAccessAvailableUsers} from "../api/api";
import {User} from "../types";

interface Props {
    open: boolean;
    onClose: () => void;
    courseId: number;
    onAssigned: () => void;
}

const AssignUserModal = ({open, onClose, courseId, onAssigned}: Props) => {
    const {showAlert} = useAlert();

    const [users, setUsers] = useState<User[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [searchEmail, setSearchEmail] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [loading, setLoading] = useState(false);
    const [assigningUserId, setAssigningUserId] = useState<number | null>(null);

    const pageSize = 10;

    const fetchAvailableUsers = async (uiPage: number, email: string) => {
        if (!courseId) return;

        try {
            setLoading(true);
            const res = await getCourseAccessAvailableUsers(courseId, uiPage - 1, pageSize, email);
            setUsers(res.users);
            setTotalPages(res.totalPages);
            setPage(uiPage);
        } catch (e: any) {
            showAlert(e?.message ?? "Failed to load available users", "error");
            setUsers([]);
            setTotalPages(1);
            setPage(1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;

        setUsers([]);
        setPage(1);
        setTotalPages(1);
        setSearchEmail("");
        setSearchInput("");

        fetchAvailableUsers(1, "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, courseId]);

    const onSearch = () => {
        const email = searchInput.trim();
        setSearchEmail(email);
        fetchAvailableUsers(1, email);
    };

    const onAssign = async (userId: number) => {
        try {
            setAssigningUserId(userId);
            await assignUserToCourse(courseId, userId);

            showAlert("User assigned successfully", "success");
            onAssigned();

            fetchAvailableUsers(1, searchEmail);
        } catch (e: any) {
            showAlert(e?.message ?? "Failed to assign user", "error");
        } finally {
            setAssigningUserId(null);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "min(1000px, 100vw)",
                    maxHeight: "100vh",
                    overflow: "auto",
                    outline: "none",
                    p: 2,
                }}
            >
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: "hidden",
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            px: 2,
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                        })}
                    >
                        <Typography sx={{fontWeight: 800}} noWrap>
                            Assign User
                        </Typography>

                        <Box sx={{flexGrow: 1}}/>

                        <IconButton
                            onClick={onClose}
                            sx={(theme) => ({
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.divider}`,
                            })}
                        >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                    <Box
                        sx={(theme) => ({
                            px: 2,
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                        })}
                    >
                        <TextField
                            type="search"
                            size="small"
                            placeholder="Search by email…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") onSearch();
                            }}
                            sx={{width: 320}}
                        />

                        <IconButton
                            onClick={onSearch}
                            disabled={loading}
                            sx={(theme) => ({
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.divider}`,
                            })}
                        >
                            <SearchIcon fontSize="small"/>
                        </IconButton>

                        <Box sx={{flexGrow: 1}}/>

                        <Typography variant="body2" color="text.secondary">
                            Page {page} of {totalPages}
                        </Typography>
                    </Box>
                    {loading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                            <CircularProgress/>
                        </Box>
                    ) : users.length === 0 ? (
                        <Box sx={{py: 6, textAlign: "center"}}>
                            <Typography variant="body1">No users found</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Try another email or change page.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{width: "100%", overflowX: "auto"}}>
                            <table style={{width: "100%", borderCollapse: "collapse"}}>
                                <thead>
                                <tr>
                                    <AppTableHeaderCell>Full Name</AppTableHeaderCell>
                                    <AppTableHeaderCell>Email</AppTableHeaderCell>
                                    <AppTableHeaderCell align="right">Action</AppTableHeaderCell>
                                </tr>
                                </thead>

                                <tbody>
                                {users.map((u) => {
                                    const assigning = assigningUserId === u.id;

                                    return (
                                        <tr key={u.id}>
                                            <td style={{padding: "12px 16px"}}>
                                                <Typography variant="body2">{u.fullName || "—"}</Typography>
                                            </td>
                                            <td style={{padding: "12px 16px"}}>
                                                <Typography variant="body2">{u.email}</Typography>
                                            </td>
                                            <td style={{padding: "12px 16px", textAlign: "right"}}>
                                                <IconButton
                                                    onClick={() => onAssign(u.id)}
                                                    disabled={assigning}
                                                    aria-label="assign"
                                                    size="small"
                                                >
                                                    {assigning ? (
                                                        <CircularProgress size={18}/>
                                                    ) : (
                                                        <AddCircleIcon color="success"/>
                                                    )}
                                                </IconButton>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </Box>
                    )}

                    <Box sx={{display: "flex", justifyContent: "center", mt: 3, pb: 2}}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, value) => fetchAvailableUsers(value, searchEmail)}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>
                </Paper>
            </Box>
        </Modal>
    );
};

export default AssignUserModal;
